import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Chart from 'react-apexcharts'
import { RPM_FIELD_COUNT, RPM_FIELD_LETTERS, generateRpmFieldName } from '../../constants/rpmFieldConstants'

// Constants
const CHART_HEIGHT = 350
const CHART_COLOR = '#ff0000'
const CHART_PADDING = {
  top: 30,    // Space for chart title
  bottom: 40,  // Space for x-axis labels
}
const THROTTLE_MS = 20 // ~50fps
const DATA_POINTS_COUNT = RPM_FIELD_COUNT

function RpmFieldChart({ 
  chartId, 
  maxRpm = 3000, 
  rpmFieldData = Array(DATA_POINTS_COUNT).fill(100),
  onDataChange,
  disabled = false, 
}) {
  const [dataPoints, setDataPoints] = useState(rpmFieldData)
  const chartRef = useRef(null)
  const isMouseDown = useRef(false)
  const lastMoveTime = useRef(0)

  // Generate RPM points based on maxRpm
  const rpmPoints = useMemo(() => 
    Array.from({ length: DATA_POINTS_COUNT }, (_, i) => 
      Math.round((i * maxRpm) / (DATA_POINTS_COUNT - 1)),
    ), [maxRpm])

  // Update dataPoints when external data changes
  useEffect(() => {
    setDataPoints(rpmFieldData)
  }, [rpmFieldData])

  // Notify parent of data changes (separate from state updates to avoid render-time updates)
  const notifyDataChange = useCallback((newData) => {
    // Use setTimeout to defer the callback to avoid render-time state updates
    setTimeout(() => onDataChange?.(newData), 0)
  }, [onDataChange])

  // Extract coordinates from touch/mouse events
  const getEventCoordinates = useCallback((event, rect) => {
    const isTouch = event.type.startsWith('touch')
    return {
      x: isTouch ? event.touches[0].clientX - rect.left : event.clientX - rect.left,
      y: isTouch ? event.touches[0].clientY - rect.top : event.clientY - rect.top,
    }
  }, [])

  // Handle drag functionality
  const updateChart = useCallback((event) => {
    if (disabled || (!isMouseDown.current && !event.type.startsWith('touch'))) return

    const chartElement = chartRef.current?.querySelector('.apexcharts-svg')
    if (!chartElement) return

    const rect = chartElement.getBoundingClientRect()
    const { x: clientX, y: clientY } = getEventCoordinates(event, rect)

    // Calculate the index of the point that was clicked/dragged
    const pointWidth = rect.width / DATA_POINTS_COUNT
    const clickedPointIndex = Math.min(
      Math.max(Math.floor(clientX / pointWidth), 0),
      DATA_POINTS_COUNT - 1,
    )

    // Account for chart padding
    const topPadding = CHART_PADDING.top
    const bottomPadding = CHART_PADDING.bottom
    const effectiveHeight = rect.height - topPadding - bottomPadding

    // Calculate percentage from top to bottom (0-100)
    const percentage = 100 - ((clientY - topPadding) / effectiveHeight) * 100
    // Clamp between 0-100 and round to nearest whole number
    const newValue = Math.round(Math.max(0, Math.min(percentage, 100)))

    // Only update if the value has changed using functional update
    setDataPoints(prevDataPoints => {
      if (prevDataPoints[clickedPointIndex] !== newValue) {
        const newDataPoints = [...prevDataPoints]
        newDataPoints[clickedPointIndex] = newValue
        // Use deferred callback to avoid render-time state updates
        notifyDataChange(newDataPoints)
        return newDataPoints
      }
      return prevDataPoints
    })
  }, [disabled, getEventCoordinates, notifyDataChange])

  // Generic event handler wrapper for disabled state
  const withDisabledCheck = useCallback((handler) => (event) => {
    if (disabled) return
    handler(event)
  }, [disabled])

  // Mouse event handlers
  const handleMouseDown = useCallback((event) => {
    isMouseDown.current = true
    updateChart(event)
  }, [updateChart])

  const handleMouseMove = useCallback((event) => {
    const now = Date.now()
    if (isMouseDown.current && now - lastMoveTime.current > THROTTLE_MS) {
      // Throttle to ~50fps
      lastMoveTime.current = now
      updateChart(event)
    }
  }, [updateChart])

  const handleMouseUp = useCallback(() => {
    isMouseDown.current = false
  }, [])

  // Touch event handlers
  const handleTouchStart = useCallback((event) => {
    isMouseDown.current = true
    event.preventDefault() // Prevent scrolling
    updateChart(event)
  }, [updateChart])

  const handleTouchMove = useCallback((event) => {
    if (isMouseDown.current) {
      event.preventDefault() // Prevent scrolling
      updateChart(event)
    }
  }, [updateChart])

  const handleTouchEnd = useCallback(() => {
    isMouseDown.current = false
  }, [])

  // Setup event listeners
  useEffect(() => {
    const chartContainer = chartRef.current
    if (!chartContainer) return

    // Apply disabled check wrapper to handlers
    const mouseDownHandler = withDisabledCheck(handleMouseDown)
    const mouseMoveHandler = withDisabledCheck(handleMouseMove)
    const touchStartHandler = withDisabledCheck(handleTouchStart)
    const touchMoveHandler = withDisabledCheck(handleTouchMove)

    // Mouse events
    chartContainer.addEventListener('mousedown', mouseDownHandler)
    document.addEventListener('mousemove', mouseMoveHandler)
    document.addEventListener('mouseup', handleMouseUp)

    // Touch events
    chartContainer.addEventListener('touchstart', touchStartHandler, { passive: false })
    chartContainer.addEventListener('touchmove', touchMoveHandler, { passive: false })
    chartContainer.addEventListener('touchend', handleTouchEnd)

    return () => {
      // Cleanup event listeners
      if (chartContainer) {
        chartContainer.removeEventListener('mousedown', mouseDownHandler)
        chartContainer.removeEventListener('touchstart', touchStartHandler)
        chartContainer.removeEventListener('touchmove', touchMoveHandler)
        chartContainer.removeEventListener('touchend', handleTouchEnd)
      }
      document.removeEventListener('mousemove', mouseMoveHandler)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [withDisabledCheck, handleMouseDown, handleMouseMove, handleMouseUp, handleTouchStart, handleTouchMove, handleTouchEnd])

  // Chart options
  const chartOptions = useMemo(() => ({
    chart: {
      height: CHART_HEIGHT,
      type: 'line',
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      fontFamily: 'Roboto, sans-serif',
      // Remove ApexCharts click events since we're handling our own
      events: {},
    },
    series: [
      {
        name: 'Maximum alternator power',
        data: dataPoints,
      },
    ],
    yaxis: {
      min: 0,
      max: 100,
      title: {
        text: 'Maximum alternator power (%)',
      },
      labels: {
        formatter: (value) => `${value}%`,
      },
    },
    xaxis: {
      categories: rpmPoints,
      title: {
        text: 'Engine RPM',
      },
      tickPlacement: 'on',
    },
    colors: [CHART_COLOR],
    markers: {
      size: 12,
      colors: [CHART_COLOR],
      strokeColors: '#fff',
      strokeWidth: 3,
      hover: {
        size: 16,
        sizeOffset: 3,
      },
      discrete: dataPoints.map((_, index) => ({
        seriesIndex: 0,
        dataPointIndex: index,
        fillColor: CHART_COLOR,
        strokeColor: '#fff',
        size: 12,
      })),
    },
    tooltip: {
      enabled: true,
      custom({ series, seriesIndex, dataPointIndex }) {
        const rpm = rpmPoints[dataPointIndex]
        const power = series[seriesIndex][dataPointIndex]
        return `<div class="apexcharts-tooltip-custom">
                  <strong>RPM:</strong> ${rpm}<br/>
                  <strong>Power:</strong> ${power}%
                </div>`
      },
    },
    stroke: {
      width: 3,
      curve: 'smooth',
    },
    grid: {
      show: true,
      borderColor: '#e0e0e0',
    },
  }), [dataPoints, rpmPoints])

  return (
    <div className="rpm-field-chart">
      <div className="mb-3">
        <h4>RPM vs Field Voltage Chart {chartId ? `(${chartId})` : ''}</h4>
        {disabled && (
          <div className="alert alert-warning">
            <i className="bi bi-info-circle"></i> Chart editing is disabled. Enable alternator to edit.
          </div>
        )}
      </div>
      
      <div 
        ref={chartRef}
        className={`chart-container ${disabled ? 'chart-disabled' : ''}`}
        style={{ 
          cursor: disabled ? 'not-allowed' : 'crosshair',
          opacity: disabled ? 0.6 : 1,
          userSelect: 'none', // Prevent text selection during drag
        }}
      >
        <Chart
          options={chartOptions}
          series={chartOptions.series}
          type="line"
          height={CHART_HEIGHT}
        />
      </div>

      <div className="mt-3">
        <small className="text-muted">
          {disabled 
            ? 'Enable this alternator to edit the chart'
            : 'Click and drag on any data point to adjust the power value for that RPM'
          }
        </small>
      </div>

      {/* Hidden input fields for form submission */}
      <div style={{ display: 'none' }}>
        {dataPoints.map((value, index) => {
          const letter = RPM_FIELD_LETTERS[index]
          const fieldName = generateRpmFieldName(letter, chartId || '')
          return (
            <input
              key={fieldName}
              name={fieldName}
              value={value}
              readOnly
            />
          )
        })}
      </div>
    </div>
  )
}

export default RpmFieldChart
